import axios, { AxiosRequestConfig } from "axios";
import { stringify } from "qs";
import { compact, includes, isArray, isEmpty, isPlainObject, join, map, omit, omitBy, some } from "lodash";
import { security } from "./security";
import { message } from "../components";

const paramSerializer = (params: any, arrayFormat: "indices" | "brackets" | "repeat" | "comma") => {
  const paramsTemp = omitBy(params, (value: any) => {
    if (typeof value === "string" || value instanceof Array) {
      return isEmpty(value);
    }
  });
  return stringify(paramsTemp, { arrayFormat });
};

// Axios nests its header defaults per method (common/get/post/...); those keys are
// dropped as before so only the caller's own headers survive alongside the token.
const headerDefaultKeys = ["common", "delete", "get", "head", "post", "put", "patch"];

const authorizationSetter = async (config: AxiosRequestConfig) => {
  const token = await security.getAccessTokenSilently();
  // For the AICC admin audit trail. The backend decodes the ACCESS token, which carries no `email`
  // claim — OIDC puts that on the ID token — so every audit row it wrote named a bare Auth0 subject
  // (`auth0|606d89…`) and a language's Version History could not say who published it. The address is
  // already here in the SPA's user profile, so declaring it costs one header.
  //
  // Recorded unmarked rather than as `unverified:` on purpose: the request also carries a validated
  // token, so the subject cannot be forged and is stored alongside. A declared address only chooses
  // which name that verified account displays under.
  //
  // Spread conditionally: `user` is undefined until Auth0 finishes loading, and an empty header reads
  // to the backend as "an address was declared and it is blank".
  const adminEmail = security.getUser()?.email;
  config.headers = {
    ...omit(config.headers, headerDefaultKeys),
    Authorization: "Bearer " + token,
    ...(adminEmail ? { "X-Admin-User": adminEmail } : {}),
  };
  return config;
};

const getErrorMessage = (err: any) => {
  let _message;
  if (err.response != null) {
    const { status, data } = err.response;
    _message = data?.detail || data?.violations?.[0]?.message || data?.title;
    if (_message == null) {
      switch (status) {
        case 400:
          _message = "Bad Request";
          break;
        case 401:
          _message = "Sorry, you don't have authorization.";
          security.logout({ returnTo: window.location.origin });
          break;
        case 403:
          _message = "Sorry, you don't have access to this page.";
          break;
        case 404:
          _message = "Sorry, can’t find the specific resource.";
          break;
        case 405:
          _message = "Method is incorrect";
          break;
        case 500:
          _message = "Sorry, the server is reporting an error.";
          break;
        default:
          _message = "Sorry, there are some errors.";
      }
    }
  }
  return _message;
};

const errorHandler = (err: any) => Promise.reject(getErrorMessage(err));

const eComErrorHandler = (err: any) => {
  const defaultMessage = "There was an error occurred. Please try again later. If the problem persists, contact us.";

  const status = err?.response?.status ?? 500;
  const reason = { status, message: err?.response?.message ?? defaultMessage };

  if (status === 401) {
    security.logout({ returnTo: window.location.origin });
  }

  return Promise.reject(reason).finally(() => {
    message.error(reason).then();
  });
};

const aiccSilentNotFoundUrls = ["/aicc/quota", "/onboarding-profile"];

const aiccErrorMessage = (detail: any) => {
  if (isArray(detail)) return join(compact(map(detail, "msg")), "; ");
  if (isPlainObject(detail)) return detail.message;
  return detail;
};

const aiccErrorHandler = (err: any) => {
  const defaultMessage = "There was an error occurred. Please try again later. If the problem persists, contact us.";

  const status = err?.response?.status ?? 500;
  const failureMessage = aiccErrorMessage(err?.response?.data?.detail) || defaultMessage;
  const reason = { status, message: failureMessage };

  if (status === 401) {
    security.logout({ returnTo: window.location.origin });
  }

  const isSilentGet =
    err?.config?.method?.toLowerCase() === "get" &&
    some(aiccSilentNotFoundUrls, (path) => includes(err?.config?.url, path));

  if (status === 404 && isSilentGet) {
    return Promise.resolve(undefined);
  }

  return Promise.reject(reason).finally(() => {
    message.error(reason).then();
  });
};

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  // query params issue: https://github.com/axios/axios/issues/1727
  paramsSerializer: (params) => paramSerializer(params, "comma"),
});

const eComInstance = axios.create({
  baseURL: process.env.REACT_APP_ECOM_API_BASE_URL,
  // baseURL: "/e_com_api",
  paramsSerializer: (params) => paramSerializer(params, "repeat"),
});

const aiccInstance = axios.create({
  baseURL: process.env.REACT_APP_AICC_API_BASE_URL,
  // baseURL: "/aicc_api",
  paramsSerializer: (params) => paramSerializer(params, "repeat"),
});

instance.interceptors.request.use(authorizationSetter);
eComInstance.interceptors.request.use(authorizationSetter);
aiccInstance.interceptors.request.use(authorizationSetter);

instance.interceptors.response.use((response) => response.data, errorHandler);
eComInstance.interceptors.response.use((response) => response.data, eComErrorHandler);
aiccInstance.interceptors.response.use((response) => response.data, aiccErrorHandler);
export default instance;
export { eComInstance, aiccInstance };
